import { type FC, useCallback } from "react";
import { useSearchParams } from "react-router-dom";

import type { NodeSnapshot } from "../../types";

import { updateSearchParams } from "../../utils/searchParams";
import { formatRelativeTime } from "../../utils/time";
import { ActivePulseIndicator } from "../common/ActivePulseIndicator";
import { DataTable } from "../common/DataTable";
import { NodesEmptyState } from "../common/EmptyState";
import { HudPanel } from "../common/HudPanel";
import { HudStatusPill } from "../common/HudStatusPill";
import { StatusBadge } from "../common/StatusBadge";

const NODE_HEADER_LABELS = [
  "Node",
  "Status",
  "Last Heartbeat",
  "Active",
  "Capacity",
  "Total Assignments",
  "Last Assignment",
  "Last Completion",
  "Last Failure",
] as const;

const ActiveTasksCell: FC<{ activeTasks: number }> = ({ activeTasks }) => (
  <span className="inline-flex items-center gap-1.5">
    {activeTasks > 0 && (
      <svg
        className="h-3.5 w-3.5 text-emerald-500"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
          clipRule="evenodd"
        />
      </svg>
    )}
    {activeTasks}
  </span>
);

const NodeFailureCell: FC<{ lastFailure: number | null }> = ({ lastFailure }) =>
  lastFailure ? (
    <span className="flex items-center gap-1.5 text-rose-600 dark:text-rose-400">
      <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
          clipRule="evenodd"
        />
      </svg>
      {formatRelativeTime(lastFailure)}
    </span>
  ) : (
    <>—</>
  );

const NodeTableRow: FC<{ node: NodeSnapshot }> = ({ node }) => (
  <DataTable.Row>
    <DataTable.Cell className="font-mono text-[11px] font-bold text-slate-900 dark:text-white">
      <div className="flex items-center gap-2">
        <ActivePulseIndicator
          size="sm"
          pulse={node.status === "online"}
          glow={node.status === "online"}
          colorClassName={
            node.status === "online"
              ? "bg-emerald-500"
              : "bg-slate-400 dark:bg-slate-600"
          }
          className="rounded-full"
        />
        <span className="font-mono text-xs">{node.id}</span>
      </div>
    </DataTable.Cell>
    <DataTable.Cell>
      <StatusBadge status={node.status} size="sm" />
    </DataTable.Cell>
    <DataTable.Cell className="whitespace-nowrap text-xs text-slate-500 dark:text-slate-400">
      {formatRelativeTime(node.lastHeartbeat)}
    </DataTable.Cell>
    <DataTable.Cell className="text-xs text-slate-700 dark:text-slate-300">
      <ActiveTasksCell activeTasks={node.activeTasks} />
    </DataTable.Cell>
    <DataTable.Cell className="text-xs text-slate-700 dark:text-slate-300">
      {node.capacity}
    </DataTable.Cell>
    <DataTable.Cell className="text-xs text-slate-700 dark:text-slate-300">
      {node.totalAssignments}
    </DataTable.Cell>
    <DataTable.Cell className="whitespace-nowrap text-xs text-slate-500 dark:text-slate-400">
      {formatRelativeTime(node.lastAssignment)}
    </DataTable.Cell>
    <DataTable.Cell className="whitespace-nowrap text-xs text-slate-500 dark:text-slate-400">
      {formatRelativeTime(node.lastCompletion)}
    </DataTable.Cell>
    <DataTable.Cell className="whitespace-nowrap text-xs text-slate-500 dark:text-slate-400">
      <NodeFailureCell lastFailure={node.lastFailure} />
    </DataTable.Cell>
  </DataTable.Row>
);

const NodeTelemetryDesktopTable: FC<{ nodes: NodeSnapshot[] }> = ({
  nodes,
}) => (
  <div className="hidden md:block">
    <DataTable maxHeight="400px">
      <DataTable.Header className="sticky top-0 z-10">
        <tr>
          {NODE_HEADER_LABELS.map((label) => (
            <DataTable.HeaderCell key={label}>{label}</DataTable.HeaderCell>
          ))}
        </tr>
      </DataTable.Header>
      <DataTable.Body>
        {nodes.map((node) => (
          <NodeTableRow key={node.id} node={node} />
        ))}
      </DataTable.Body>
    </DataTable>
  </div>
);

const NodeCard: FC<{ node: NodeSnapshot }> = ({ node }) => (
  <div className="px-3 py-2.5">
    {/* Row 1: Node ID + status */}
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <ActivePulseIndicator
          size="sm"
          pulse={node.status === "online"}
          glow={node.status === "online"}
          colorClassName={
            node.status === "online"
              ? "bg-emerald-500"
              : "bg-slate-400 dark:bg-slate-600"
          }
          className="rounded-full"
        />
        <span className="font-mono text-[11px] font-bold text-slate-900 dark:text-white">
          {node.id}
        </span>
      </div>
      <StatusBadge status={node.status} size="sm" />
    </div>

    {/* Row 2: Key stats */}
    <div className="mt-1.5 grid grid-cols-3 gap-x-3 gap-y-1 text-xs">
      <Stat label="Active" value={node.activeTasks} />
      <Stat label="Capacity" value={node.capacity} />
      <Stat label="Assignments" value={node.totalAssignments} />
    </div>

    <CapacityBar active={node.activeTasks} capacity={node.capacity} />

    {/* Row 3: Timestamps */}
    <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 font-mono text-[9px] uppercase tracking-[0.15em] text-slate-400 dark:text-slate-500">
      <span>Heartbeat {formatRelativeTime(node.lastHeartbeat)}</span>
      <span>Assigned {formatRelativeTime(node.lastAssignment)}</span>
      {node.lastFailure ? (
        <span className="text-rose-500 dark:text-rose-400">
          Failed {formatRelativeTime(node.lastFailure)}
        </span>
      ) : null}
    </div>
  </div>
);

const Stat: FC<{ label: string; value: number }> = ({ label, value }) => (
  <div className="flex flex-col gap-0.5">
    <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">
      {label}
    </span>
    <span className="font-mono text-[11px] font-bold text-slate-700 dark:text-slate-300">
      {value}
    </span>
  </div>
);

const CapacityBar: FC<{ active: number; capacity: number }> = ({
  active,
  capacity,
}) => {
  const pct = capacity > 0 ? Math.min(100, (active / capacity) * 100) : 0;
  return (
    <div className="mt-2 h-1 w-full overflow-hidden bg-slate-200 dark:bg-slate-700">
      <div
        className={`h-full transition-all ${pct >= 80 ? "bg-amber-500" : "bg-emerald-500"}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
};

interface NodeTelemetryTableProps {
  nodes: NodeSnapshot[];
}

const NODE_TELEMETRY_PARAM = "overview_nodes";

export const NodeTelemetryTable: FC<NodeTelemetryTableProps> = ({ nodes }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isExpanded = searchParams.get(NODE_TELEMETRY_PARAM) === "expanded";
  const collapsed = !isExpanded;

  const toggleCollapsed = useCallback(() => {
    setSearchParams(
      (prev) =>
        updateSearchParams(prev, {
          [NODE_TELEMETRY_PARAM]: collapsed ? "expanded" : null,
        }),
      { replace: true },
    );
  }, [collapsed, setSearchParams]);

  const onlineCount = nodes.filter((n) => n.status === "online").length;

  return (
    <HudPanel
      title="Node Telemetry"
      subtitle="Cluster health and status"
      tone={onlineCount > 0 ? "success" : "neutral"}
      actions={
        <div className="flex items-center gap-2">
          <HudStatusPill
            label={
              collapsed
                ? `${onlineCount} ${onlineCount === 1 ? "node" : "nodes"} online`
                : `${nodes.length} ${nodes.length === 1 ? "node" : "nodes"} monitored`
            }
            tone={onlineCount > 0 ? "success" : "neutral"}
            pulse={onlineCount > 0 && collapsed}
          />
          <button
            type="button"
            onClick={toggleCollapsed}
            className="flex h-6 w-6 items-center justify-center text-slate-400 transition-colors hover:text-emerald-700 dark:text-slate-500 dark:hover:text-emerald-300"
            aria-label={
              collapsed ? "Expand node telemetry" : "Collapse node telemetry"
            }
          >
            <svg
              className={`h-4 w-4 transition-transform ${collapsed ? "" : "rotate-180"}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
        </div>
      }
      bodyClassName={collapsed ? "hidden" : ""}
    >
      {nodes.length === 0 ? (
        <NodesEmptyState />
      ) : (
        <>
          <div className="divide-y divide-slate-100 md:hidden dark:divide-white/5">
            {nodes.map((node) => (
              <NodeCard key={node.id} node={node} />
            ))}
          </div>

          <NodeTelemetryDesktopTable nodes={nodes} />
        </>
      )}
    </HudPanel>
  );
};
